/**
 * QuantumSoundscape - Procedural ambient quantum music generator
 * Creates deep, immersive cosmic soundscape using Web Audio API
 * Layers: Deep Drone, Ethereal Pad, Crystal Shimmer, Space Wind, Quantum Pulse
 */

class QuantumSoundscape {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.nodes = [];
    this.isPlaying = false;
    this.isMuted = false;
    this.animFrameId = null;
    this.startTime = 0;
  }

  init() {
    if (this.ctx) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();

    // Master output with compressor for smooth dynamics
    const compressor = this.ctx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-24, this.ctx.currentTime);
    compressor.knee.setValueAtTime(30, this.ctx.currentTime);
    compressor.ratio.setValueAtTime(12, this.ctx.currentTime);
    compressor.attack.setValueAtTime(0.003, this.ctx.currentTime);
    compressor.release.setValueAtTime(0.25, this.ctx.currentTime);

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.setValueAtTime(0, this.ctx.currentTime);
    this.masterGain.connect(compressor);
    compressor.connect(this.ctx.destination);
  }

  start() {
    if (this.isPlaying) return;
    this.init();

    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    this.startTime = this.ctx.currentTime;
    this.isPlaying = true;

    // Fade in master volume over 3 seconds
    this.masterGain.gain.setValueAtTime(0, this.ctx.currentTime);
    this.masterGain.gain.linearRampToValueAtTime(0.7, this.ctx.currentTime + 3);

    this._createDeepDrone();
    this._createEtherealPad();
    this._createCrystalShimmer();
    this._createSpaceWind();
    this._createQuantumPulse();
    this._createBinauralBeats();
    this._startModulation();
  }

  /**
   * Layer 1: Deep Bass Drone
   * Two detuned sub-bass oscillators creating a warm, powerful foundation
   */
  _createDeepDrone() {
    const now = this.ctx.currentTime;

    // Fundamental drone - A1 (55Hz)
    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(55, now);
    const gain1 = this.ctx.createGain();
    gain1.gain.setValueAtTime(0.18, now);

    // Fifth above - E2 (82.4Hz)
    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(82.41, now);
    osc2.detune.setValueAtTime(-8, now);
    const gain2 = this.ctx.createGain();
    gain2.gain.setValueAtTime(0.12, now);

    // Sub-bass - A0 (27.5Hz) for body feel
    const osc3 = this.ctx.createOscillator();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(27.5, now);
    const gain3 = this.ctx.createGain();
    gain3.gain.setValueAtTime(0.08, now);

    // Low-pass filter to keep it smooth
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.setValueAtTime(200, now);
    lpf.Q.setValueAtTime(1, now);

    osc1.connect(gain1);
    osc2.connect(gain2);
    osc3.connect(gain3);
    gain1.connect(lpf);
    gain2.connect(lpf);
    gain3.connect(lpf);
    lpf.connect(this.masterGain);

    osc1.start(now);
    osc2.start(now);
    osc3.start(now);

    this.nodes.push(osc1, osc2, osc3, gain1, gain2, gain3, lpf);
    this._droneGains = [gain1, gain2, gain3];
  }

  /**
   * Layer 2: Ethereal Pad
   * Detuned sawtooth oscillators filtered to create cosmic pad sound
   */
  _createEtherealPad() {
    const now = this.ctx.currentTime;
    // A minor chord: A3, C4, E4 - with octave doublings
    const freqs = [220, 261.63, 329.63, 440, 523.25];
    const padGains = [];

    // Reverb simulation via feedback delay
    const delay1 = this.ctx.createDelay(2);
    delay1.delayTime.setValueAtTime(0.4, now);
    const delay2 = this.ctx.createDelay(2);
    delay2.delayTime.setValueAtTime(0.7, now);
    const fb1 = this.ctx.createGain();
    fb1.gain.setValueAtTime(0.3, now);
    const fb2 = this.ctx.createGain();
    fb2.gain.setValueAtTime(0.2, now);

    const padMaster = this.ctx.createGain();
    padMaster.gain.setValueAtTime(0.06, now);

    // Band-pass filter for ethereal quality
    const bpf = this.ctx.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.setValueAtTime(600, now);
    bpf.Q.setValueAtTime(0.8, now);

    freqs.forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, now);
      osc.detune.setValueAtTime((Math.random() - 0.5) * 20, now);

      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.04 - i * 0.005, now);

      osc.connect(g);
      g.connect(bpf);
      osc.start(now);

      this.nodes.push(osc, g);
      padGains.push(g);
    });

    bpf.connect(padMaster);

    // Delay feedback network for reverb
    padMaster.connect(delay1);
    delay1.connect(fb1);
    fb1.connect(delay2);
    delay2.connect(fb2);
    fb2.connect(delay1);
    delay1.connect(this.masterGain);
    delay2.connect(this.masterGain);
    padMaster.connect(this.masterGain);

    this.nodes.push(bpf, padMaster, delay1, delay2, fb1, fb2);
    this._padFilter = bpf;
    this._padGains = padGains;
  }

  /**
   * Layer 3: Crystal Shimmer
   * High-frequency sine tones with random timing - creates sparkle
   */
  _createCrystalShimmer() {
    const now = this.ctx.currentTime;
    this._shimmerGain = this.ctx.createGain();
    this._shimmerGain.gain.setValueAtTime(0.03, now);

    const hpf = this.ctx.createBiquadFilter();
    hpf.type = 'highpass';
    hpf.frequency.setValueAtTime(2000, now);

    this._shimmerGain.connect(hpf);
    hpf.connect(this.masterGain);

    this.nodes.push(this._shimmerGain, hpf);

    // Schedule random crystal tones
    this._shimmerInterval = setInterval(() => {
      if (!this.isPlaying || this.isMuted) return;
      this._playShimmerNote();
    }, 1500);
  }

  _playShimmerNote() {
    if (!this.ctx || this.ctx.state !== 'running') return;
    const now = this.ctx.currentTime;
    // Pentatonic scale high notes for pleasant shimmer
    const notes = [1318.5, 1567.98, 1760, 2093, 2349.3, 2637, 3135.96];
    const freq = notes[Math.floor(Math.random() * notes.length)];

    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);

    const env = this.ctx.createGain();
    const vol = Math.random() * 0.06 + 0.01;
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(vol, now + 0.05);
    env.gain.exponentialRampToValueAtTime(0.001, now + 1.5 + Math.random());

    osc.connect(env);
    env.connect(this._shimmerGain);
    osc.start(now);
    osc.stop(now + 2);
  }

  /**
   * Layer 4: Space Wind
   * Filtered white noise creating atmospheric texture
   */
  _createSpaceWind() {
    const now = this.ctx.currentTime;
    const bufferSize = this.ctx.sampleRate * 4;
    const buffer = this.ctx.createBuffer(2, bufferSize, this.ctx.sampleRate);

    // Generate pink-ish noise (more natural than white)
    for (let ch = 0; ch < 2; ch++) {
      const data = buffer.getChannelData(ch);
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.05;
        b6 = white * 0.115926;
      }
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;

    // Sweeping band-pass for wind-like movement
    const windFilter = this.ctx.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.setValueAtTime(400, now);
    windFilter.Q.setValueAtTime(2, now);

    const windGain = this.ctx.createGain();
    windGain.gain.setValueAtTime(0.15, now);

    noise.connect(windFilter);
    windFilter.connect(windGain);
    windGain.connect(this.masterGain);

    noise.start(now);

    this.nodes.push(noise, windFilter, windGain);
    this._windFilter = windFilter;
    this._windGain = windGain;
  }

  /**
   * Layer 5: Quantum Pulse
   * Rhythmic sub-bass pulse every ~4 seconds - gives a "heartbeat of the universe" feel
   */
  _createQuantumPulse() {
    this._pulseInterval = setInterval(() => {
      if (!this.isPlaying || this.isMuted || !this.ctx || this.ctx.state !== 'running') return;
      this._triggerPulse();
    }, 4000);
  }

  _triggerPulse() {
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(40, now);
    osc.frequency.exponentialRampToValueAtTime(30, now + 2);

    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(0.15, now + 0.3);
    env.gain.exponentialRampToValueAtTime(0.001, now + 2.5);

    osc.connect(env);
    env.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + 3);
  }

  /**
   * Layer 6: Binaural Beats
   * Subtle theta-wave frequency difference for meditative state (6Hz difference)
   */
  _createBinauralBeats() {
    const now = this.ctx.currentTime;

    // Left ear: 200Hz
    const oscL = this.ctx.createOscillator();
    oscL.type = 'sine';
    oscL.frequency.setValueAtTime(200, now);

    // Right ear: 206Hz (6Hz theta wave difference)
    const oscR = this.ctx.createOscillator();
    oscR.type = 'sine';
    oscR.frequency.setValueAtTime(206, now);

    const panL = this.ctx.createStereoPanner();
    panL.pan.setValueAtTime(-1, now);
    const panR = this.ctx.createStereoPanner();
    panR.pan.setValueAtTime(1, now);

    const binGain = this.ctx.createGain();
    binGain.gain.setValueAtTime(0.03, now);

    oscL.connect(panL);
    oscR.connect(panR);
    panL.connect(binGain);
    panR.connect(binGain);
    binGain.connect(this.masterGain);

    oscL.start(now);
    oscR.start(now);

    this.nodes.push(oscL, oscR, panL, panR, binGain);
  }

  /**
   * Continuous modulation loop for organic movement
   */
  _startModulation() {
    const modulate = () => {
      if (!this.isPlaying || !this.ctx) return;
      const t = this.ctx.currentTime - this.startTime;

      // Slowly sweep the pad filter frequency
      if (this._padFilter) {
        const padFreq = 500 + Math.sin(t * 0.1) * 300 + Math.sin(t * 0.07) * 150;
        this._padFilter.frequency.setTargetAtTime(padFreq, this.ctx.currentTime, 0.5);
      }

      // Sweep wind filter for movement
      if (this._windFilter) {
        const windFreq = 400 + Math.sin(t * 0.05) * 250 + Math.cos(t * 0.03) * 100;
        this._windFilter.frequency.setTargetAtTime(windFreq, this.ctx.currentTime, 0.3);
      }

      // Subtle drone volume breathing
      if (this._droneGains) {
        const breathe = 0.85 + Math.sin(t * 0.15) * 0.15;
        this._droneGains.forEach(g => {
          const base = g.gain.value;
          g.gain.setTargetAtTime(base * breathe, this.ctx.currentTime, 0.5);
        });
      }

      this.animFrameId = requestAnimationFrame(modulate);
    };
    this.animFrameId = requestAnimationFrame(modulate);
  }

  setVolume(val) {
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(
        Math.max(0, Math.min(1, val)),
        this.ctx.currentTime + 0.3
      );
    }
  }

  mute() {
    this.isMuted = true;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.5);
    }
  }

  unmute() {
    this.isMuted = false;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(0.7, this.ctx.currentTime + 0.5);
    }
  }

  stop() {
    this.isPlaying = false;

    if (this._shimmerInterval) clearInterval(this._shimmerInterval);
    if (this._pulseInterval) clearInterval(this._pulseInterval);
    if (this.animFrameId) cancelAnimationFrame(this.animFrameId);

    // Fade out
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 1);
    }

    // Stop all oscillators/sources after fade
    setTimeout(() => {
      this.nodes.forEach(node => {
        try {
          if (node.stop) node.stop();
          node.disconnect();
        } catch (error) {
          console.debug('Quantum soundscape node already stopped', error);
        }
      });
      this.nodes = [];
      if (this.ctx && this.ctx.state !== 'closed') {
        this.ctx.close().catch(() => {});
      }
      this.ctx = null;
      this.masterGain = null;
    }, 1200);
  }
}

export default QuantumSoundscape;
